import {
  AuthRequestOptions,
  BackstageIdentityApi,
  OAuthApi,
  OAuthScope,
  OpenIdConnectApi,
  ProfileInfo,
  ProfileInfoApi,
  SessionApi,
  SessionState,
} from '@backstage/core-plugin-api';
import { OAuth2, OAuth2CreateOptions } from '@backstage/core-app-api';
import { BackstageIdentityResponse } from '@backstage/plugin-auth-node';
import { Observable } from '@backstage/types';
import axios from 'axios';
import { JwtHelper } from '../util/jwtUtil';
import { ConfigHelper } from '@devxdiscover/devhub-common';

/**
 * OAuth wrapper class for DevHub
 */
export class DevHubOAuth
  implements
    OAuthApi,
    OpenIdConnectApi,
    ProfileInfoApi,
    BackstageIdentityApi,
    SessionApi
{
  private readonly oauth2: OAuth2;
  private readonly cache: Map<string, any> = new Map();

  constructor(options: OAuth2CreateOptions) {
    this.oauth2 = OAuth2.create({
      discoveryApi: options.discoveryApi,
      oauthRequestApi: options.oauthRequestApi,
      provider: {
        id: options?.provider?.id ?? 'devhub-azure-auth',
        title: 'DevHub SSO',
        icon: () => null,
      },
      environment: options.configApi?.getOptionalString('auth.environment'),
    });
  }

  /**
   * @inheritDoc
   */
  async getAccessToken(
    scope?: OAuthScope,
    options?: AuthRequestOptions,
  ): Promise<string> {
    return await this.oauth2.getAccessToken(scope, options);
  }

  /**
   * @inheritDoc
   */
  async getBackstageIdentity(
    options?: AuthRequestOptions,
  ): Promise<BackstageIdentityResponse | undefined> {
    const identity = await this.oauth2.getBackstageIdentity(options);
    if (identity && identity.token) {
      // See if the token is cached in memory (this is not Redis). The purpose for this is to reduce
      // calls to the ownership service.
      const cachedIdentity = this.cache.get(identity.token);
      if (cachedIdentity) {
        return cachedIdentity;
      }

      const result = JwtHelper.unsafeDecodePayload<{ tokenId: string }>(
        identity?.token ?? '',
      );

      // If we have a tokenId, we need to get the new token from the ownership service
      if (result?.tokenId) {
        const baseUrl = ConfigHelper.instance.apiUrl('ownership/user');
        const response = await axios.get(
          `${baseUrl.toString()}?id=${result.tokenId}`,
        );
        if (response && response.data) {
          const fullIdentity = JwtHelper.unsafeDecodePayload<{ ent: string[] }>(
            response.data,
          );
          // Extract the ownershipEntityRefs from the token and add it to the identity
          if (fullIdentity?.ent) {
            identity.identity.ownershipEntityRefs = fullIdentity.ent;
          }
        }
        // Cache the identity
        this.cache.set(identity.token, identity);
      }
    }
    return identity;
  }

  /**
   * @inheritDoc
   */
  async getIdToken(options?: AuthRequestOptions): Promise<string> {
    return this.oauth2.getIdToken(options);
  }

  /**
   * @inheritDoc
   */
  async getProfile(
    options?: AuthRequestOptions,
  ): Promise<ProfileInfo | undefined> {
    return this.oauth2.getProfile(options);
  }

  /**
   * @inheritDoc
   */
  sessionState$(): Observable<SessionState> {
    return this.oauth2.sessionState$();
  }

  /**
   * @inheritDoc
   */
  async signIn(): Promise<void> {
    return await this.oauth2.signIn();
  }

  /**
   * @inheritDoc
   */
  async signOut(): Promise<void> {
    return await this.oauth2.signOut();
  }
}
